/**
 * Where the emailed reset link lands: /reset-password?token=...
 *
 * On success every session is destroyed server-side, including any the attacker
 * held — so the user must sign in again with the new password. That is the point
 * of a reset, not an inconvenience to smooth over.
 */
import { useState, type FormEvent } from 'react';
import { ApiError, resetPassword } from '../lib/api.js';
import { c, primaryButton } from '../theme.js';
import { AuthLayout, ErrorBox, Field, NoticeBox, TextLink } from './AuthLayout.js';

interface Props {
  token: string;
  /** Clears the token from the URL and returns to the sign-in screen. */
  onDone: () => void;
}

export function ResetPasswordScreen({ token, onDone }: Props) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Those passwords do not match');
      return;
    }

    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout heading="Password changed">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <NoticeBox>
            Your password has been changed, and every device that was signed in has been signed out.
          </NoticeBox>
          <button type="button" onClick={onDone} style={primaryButton}>
            Sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      heading="Choose a new password"
      intro="Once it’s changed, everything signed in to this account will be signed out."
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field
          id="password"
          labelText="New password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          autoFocus
          hint="At least 10 characters. Length beats symbols."
        />
        <Field
          id="confirm"
          labelText="Confirm new password"
          type="password"
          placeholder="••••••••"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
        />

        {error && <ErrorBox>{error}</ErrorBox>}

        <button
          type="submit"
          disabled={busy}
          style={{ ...primaryButton, marginTop: 4, opacity: busy ? 0.65 : 1 }}
        >
          {busy ? 'Saving…' : 'Change my password'}
        </button>

        <p style={{ fontSize: 12, color: c.faint, textAlign: 'center', margin: '2px 0 0' }}>
          Link expired? <TextLink onClick={onDone}>Request a new one</TextLink>
        </p>
      </form>
    </AuthLayout>
  );
}
